import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getVmiLocationState } from "@insite/client-framework/Store/Data/VmiLocations/VmiLocationsSelectors";
import updateSearchFields from "@insite/client-framework/Store/Pages/OrderHistory/Handlers/UpdateSearchFields";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Tag, { horizontalStyles, TagPresentationProps } from "@insite/mobius/Tag";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import * as React from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

const mapStateToProps = (state: ApplicationState) => {
    const vmiLocationId = state.pages.orderHistory.getOrdersParameter.vmiLocationId;

    return {
        filtersOpen: state.pages.orderHistory.filtersOpen,
        getOrdersParameter: state.pages.orderHistory.getOrdersParameter,
        vmiLocationState: vmiLocationId ? getVmiLocationState(state, vmiLocationId) : null,
    };
};

const mapDispatchToProps = {
    updateSearchFields,
};

export interface OrderHistorySearchTagsStyles {
    appliedFiltersContainer?: InjectableCss;
    appliedFilterTag?: TagPresentationProps;
}

export const orderHistorySearchTagsStyles: OrderHistorySearchTagsStyles = {
    appliedFiltersContainer: {
        css: css`
            border-top: 1px solid #E4E8F0;
            padding-top: 5px;
            margin-top: -5px;
            position: relative;
            z-index: 1;
            
            .applied-filter-title {
                color: #333F48;
                font-family: 'Roboto',sans-serif;
                font-size: 14px;
                font-weight: 600;
                margin: 6px 10px 6px 0;
                display:inline-flex;
                max-width: 260px;
            }

            > div {
                background-color: #E5EEF7;
                border: 1px solid #2060AD;
                padding: 2px 0 2px 5px;
                min-height: 2px;
                border-radius: 0;
                font-size: 14px;
                line-height: 18px;
                display: inline-flex;
                margin: 6px 10px 6px 0;

                button {
                    background-color: #E5EEF7;
                    height: 18px;
                    position: relative;
                    top: -1px;
                    
                    &:hover,
                    &:focus,
                    &:active {
                        background: none;
                        border: none;
                    }

                    svg {
                        height: 18px;
                        width: 18px;
                    }
                }
            }
        `,
    },
};

const styles = orderHistorySearchTagsStyles;

type Props = WidgetProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

const orderHistorySearchTags = ({ getOrdersParameter, updateSearchFields, vmiLocationState }: Props) => {
    const fieldRemoveHandler = (fieldName: string, value = "") => {
        updateSearchFields({ [fieldName]: value });
    };

    const customer = getOrdersParameter.customerSequence
        ? getOrdersParameter.customerSequence
        : translate("Billing Address");
    
    return (
        <>
            {getOrdersParameter.productErpNumber || getOrdersParameter.poNumber || getOrdersParameter.orderNumber || getOrdersParameter.fromDate || getOrdersParameter.toDate ||
                customer !== "-1" || getOrdersParameter.status || getOrdersParameter.orderTotalOperator || getOrdersParameter.vmiLocationId ? (
                <StyledWrapper {...styles.appliedFiltersContainer}>
                    <Typography as="span" className="applied-filter-title">Filters:</Typography>
                    {getOrdersParameter.productErpNumber && (
                        <Tag
                            {...styles.appliedFilterTag}
                            onDelete={() => {
                                fieldRemoveHandler("productErpNumber");
                            }}
                        >
                            {`${translate("Product")}: ${getOrdersParameter.productErpNumber}`}
                        </Tag>
                    )}
                    {getOrdersParameter.poNumber && (
                        <Tag
                            {...styles.appliedFilterTag}
                            onDelete={() => {
                                fieldRemoveHandler("poNumber");
                            }}
                        >
                            {`${translate("PO #")}: ${getOrdersParameter.poNumber}`}
                        </Tag>
                    )}
                    {getOrdersParameter.orderNumber && (
                        <Tag
                            {...styles.appliedFilterTag}
                            onDelete={() => {
                                fieldRemoveHandler("orderNumber");
                            }}
                        >
                            {`${translate("Order #")}: ${getOrdersParameter.orderNumber}`}
                        </Tag>
                    )}
                    {getOrdersParameter.fromDate && (
                        <Tag
                            {...styles.appliedFilterTag}
                            onDelete={() => {
                                fieldRemoveHandler("fromDate");
                            }}
                            data-test-selector="orderHistoryTags_FromDate"
                        >
                            {`${translate("From")}: ${getOrdersParameter.fromDate}`}
                        </Tag>
                    )}
                    {getOrdersParameter.toDate && (
                        <Tag
                            {...styles.appliedFilterTag}
                            onDelete={() => {
                                fieldRemoveHandler("toDate");
                            }}
                        >
                            {`${translate("To")}: ${getOrdersParameter.toDate}`}
                        </Tag>
                    )}
                    {customer !== "-1" && (
                        <Tag
                            {...styles.appliedFilterTag}
                            onDelete={() => {
                                fieldRemoveHandler("customerSequence", "-1");
                            }}
                        >
                            {`${translate("Customer")}: ${customer}`}
                        </Tag>
                    )}
                    {getOrdersParameter.status && (
                        <Tag
                            {...styles.appliedFilterTag}
                            onDelete={() => {
                                fieldRemoveHandler("status");
                            }}
                        >
                            {`${translate("Status")}: ${getOrdersParameter.displayName
                                    ? translate(getOrdersParameter.displayName!)
                                    : getOrdersParameter.status
                                }`}
                        </Tag>
                    )}
                    {getOrdersParameter.orderTotalOperator && getOrdersParameter.orderTotal && (
                        <Tag
                            {...styles.appliedFilterTag}
                            onDelete={() => {
                                fieldRemoveHandler("orderTotalOperator");
                                fieldRemoveHandler("orderTotal");
                            }}
                        >
                            {`${translate("Order Total")}: ${getOrdersParameter.orderTotalOperator} ${getOrdersParameter.orderTotal
                                }`}
                        </Tag>
                    )}
                    {getOrdersParameter.vmiLocationId && vmiLocationState?.value && (
                        <Tag
                            {...styles.appliedFilterTag}
                            onDelete={() => {
                                fieldRemoveHandler("vmiLocationId");
                            }}
                        >
                            {`${translate("VMI Location")}: ${vmiLocationState.value?.name}`}
                        </Tag>
                    )}
                </StyledWrapper>
            ) : ''}
        </>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(orderHistorySearchTags),
    definition: {
        group: "Order History",
        displayName: "Tags",
        allowedContexts: ["OrderHistoryPage", "VmiOrderHistoryPage"],
    },
};

export default widgetModule;
